import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@waymail/ui';
import { api } from './api';
import { CHAVE_CALENDARIO, chaveDaOcorrencia, horaCurta, type OcorrenciaDaApi } from './calendario';

/**
 * Lembretes de evento (ADR 0071), so com o app aberto: busca as ocorrencias
 * das proximas horas, agenda um setTimeout por lembrete e no horario mostra
 * um toast e, se a permissao ja foi dada, uma Notification. Nunca pede
 * permissao sozinho. Sem push, e-mail nem servidor agendador.
 */

const HORAS_24 = 24 * 60 * 60 * 1000;
/** Lembretes das proximas 24 h; o de 1 dia antes precisa ver eventos ate 48 h. */
const JANELA_MS = HORAS_24 + 1440 * 60_000;
const RECARGA_MS = 5 * 60_000;
const GUARDADOS = 'waymail:avisos-de-evento';

/** Avisos ja dados (chave -> inicio em ms), no localStorage: vale entre abas e recargas. */
function lerAvisados(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(GUARDADOS) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}

/** `false` se a ocorrencia ja foi avisada (aqui ou em outra aba); senao marca. */
function marcarAviso(chave: string, inicio: number): boolean {
  const avisados = lerAvisados();
  if (chave in avisados) return false;
  const limite = Date.now() - 2 * HORAS_24;
  const vivos = Object.fromEntries(Object.entries(avisados).filter(([, ms]) => ms > limite));
  try {
    localStorage.setItem(GUARDADOS, JSON.stringify({ ...vivos, [chave]: inicio }));
  } catch {
    // Sem armazenamento (modo privado): o aviso sai, so nao fica lembrado.
  }
  return true;
}

function descricao(o: OcorrenciaDaApi): string {
  if (o.allDay) {
    const dia = new Date(o.inicio).toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return `Dia inteiro, ${dia}`;
  }
  return `Começa às ${horaCurta(o.inicio)}`;
}

export function useAvisosDeEvento() {
  const toast = useToast();
  const consulta = useQuery({
    queryKey: [...CHAVE_CALENDARIO, 'avisos'],
    queryFn: () => {
      const agora = Date.now();
      const from = encodeURIComponent(new Date(agora).toISOString());
      const to = encodeURIComponent(new Date(agora + JANELA_MS).toISOString());
      return api<{ items: OcorrenciaDaApi[] }>(`/calendar/events?from=${from}&to=${to}`);
    },
    refetchInterval: RECARGA_MS,
    refetchOnWindowFocus: true,
  });
  const dados = consulta.data;
  // Dado vindo do cache em disco pode ter evento ja excluido: so agenda o que veio agora.
  const fresco = consulta.isFetchedAfterMount;

  useEffect(() => {
    if (!fresco || dados === undefined) return;
    const agora = Date.now();
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (const o of dados.items ?? []) {
      if (o.reminderMinutes === null) continue;
      const inicio = Date.parse(o.inicio);
      const hora = inicio - o.reminderMinutes * 60_000;
      // Depois do inicio nao avisa mais; antes dele, o atrasado sai na hora.
      if (Math.max(hora, agora) > inicio || hora - agora > HORAS_24) continue;
      timers.push(
        setTimeout(
          () => {
            if (!marcarAviso(chaveDaOcorrencia(o), inicio)) return;
            toast({ title: `Lembrete: ${o.title}`, description: descricao(o) });
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              try {
                new Notification(o.title, { body: descricao(o) });
              } catch {
                // Alguns navegadores (Android) so notificam pelo service worker.
              }
            }
          },
          Math.max(0, hora - agora),
        ),
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [dados, fresco, toast]);
}
